import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import {
  ExternalOrderDetails,
  fetchExternalOrderDetails,
  updateSampleOrderStatus,
} from './services/sampleAnalysisAPI';
import styles from './SampleOrderDetailsPage.module.css';
import { MOCK_ORDER_DETAILS } from './services/mockData';

const USE_MOCK_DATA = false;
const SIZES = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

const ExpandableText: React.FC<{ text: string | null | undefined; limit?: number }> = ({ text, limit = 100 }) => {
  const [expanded, setExpanded] = useState(false);
  const safeText = text || '';
  const isLong = safeText.length > limit;

  return (
    <div className={styles.expandableContainer}>
      <strong className={expanded ? styles.expandedText : styles.truncatedText}>
        {safeText || '--'}
      </strong>
      {isLong && (
        <button
          type="button"
          className={styles.toggleTextBtn}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      )}
    </div>
  );
};

const SampleOrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAws = user?.isAws === true;

  const [orderDetails, setOrderDetails] = useState<ExternalOrderDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; status: 0 | 2 | null }>({ open: false, status: null });

  const loadDetails = async () => {
    if (!id) return;
    try {
      setLoadingDetails(true);
      setError(null);
      if (USE_MOCK_DATA) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        setOrderDetails(MOCK_ORDER_DETAILS[Number(id)] || null);
        return;
      }
      const response = await fetchExternalOrderDetails(Number(id));
      setOrderDetails(response.data);
    } catch (err) {
      console.error(err);
      setError('Falha ao carregar detalhes do pedido de amostra.');
      setOrderDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [id]);

  const openConfirmDialog = (status: 0 | 2) => {
    setConfirmDialog({ open: true, status });
  };

  const executeUpdateStatus = async () => {
    if (!id || confirmDialog.status === null) return;

    try {
      setConfirmDialog(prev => ({ ...prev, open: false }));
      setUpdating(true);
      await updateSampleOrderStatus(Number(id), confirmDialog.status);
      await loadDetails();
    } catch (err) {
      console.error(err);
      alert('Ocorreu um erro ao atualizar o status do pedido.');
    } finally {
      setUpdating(false);
      setConfirmDialog({ open: false, status: null });
    }
  };


  const formatCurrency = (value?: number) => {
    if (typeof value !== 'number') return '--';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const getBadgeProps = (pedido_situacao: number | null | undefined, defaultDesc: string | null | undefined) => {
    if (pedido_situacao === 1) {
      return { text: 'Aguardando Amostras', className: styles.statusOpen };
    }
    if (pedido_situacao === 3) {
      return { text: 'Finalizado', className: styles.statusDone };
    }
    if (pedido_situacao === 2) {
      return { text: 'Recusado', className: styles.statusCanceled };
    }
    return { text: defaultDesc || String(pedido_situacao || '--'), className: styles.statusOpen };
  };

  const DOT_COLORS: Record<string, string> = {
    respondida: '#16a34a',
    disponivel: '#ea580c',
    aguardando: '#dc2626',
  };
  const ETAPA_LABELS_DOT = ['10d', '30d', '60d', 'Fim'];

  const renderEtapasDots = (order: any) => {
    const raw = order.etapas_status;
    if (!raw) {
      return (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {ETAPA_LABELS_DOT.map((label, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#d1d5db' }} />
              <span style={{ fontSize: '0.6rem', color: '#9ca3af', fontWeight: 600 }}>{label}</span>
            </div>
          ))}
        </div>
      );
    }
    const statuses = raw.split(',');
    return (
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        {statuses.map((s: string, i: number) => {
          const color = DOT_COLORS[s.trim()] || '#d1d5db';
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                backgroundColor: color,
                boxShadow: s.trim() === 'respondida' ? `0 0 0 2px ${color}40` : undefined,
              }} />
              <span style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>{ETAPA_LABELS_DOT[i]}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const formatCNPJ = (val?: string | null) => {
    if (!val) return '--';
    const cleaned = val.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return val;
  };

  const formatPhone = (val?: string | null) => {
    if (!val) return '--';
    const cleaned = val.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return val;
  };

  const calculatedTotal = useMemo(() => {
    return (orderDetails?.itens_pedido || []).reduce((acc: number, item: any) => acc + Number(item.subtotal || 0), 0);
  }, [orderDetails]);

  const selectedOrder = orderDetails?.pedido_externo;
  const sample = orderDetails?.amostra_pedido;
  const client = orderDetails?.cliente;
  const orderItems = orderDetails?.itens_pedido || [];

  return (
    <div className={styles.pageContainer}>
      <div className={styles.pageHeader}>
        <button className={styles.backButton} onClick={() => navigate('/amostras-pedidos')}>
          <ArrowBackIcon fontSize="small" /> Voltar
        </button>
        <h1>Detalhes do Pedido #{selectedOrder?.id || id}</h1>
      </div>

      <div className={styles.detailsCard}>
        <div className={styles.detailsHeader}>
          <div>
            <h3>Detalhes completos</h3>
          </div>
          <div className={styles.headerActionsGroup}>
            {/* Botão de Ficha de Avaliação: visível quando a ficha existe (tem status_avaliacao) */}
            {selectedOrder?.status_avaliacao != null && (
              <button
                className={styles.btnEvaluation}
                onClick={() => navigate(`/amostras-pedidos/${id}/avaliacao`)}
                title="Avaliação em Etapas (10/30/60 dias)"
              >
                <AssignmentIcon fontSize="small" />
                Avaliação em Etapas
              </button>
            )}
            {!isAws && selectedOrder?.pedido_situacao === 1 && (
              <div className={styles.approvalButtons}>
                <button
                  className={styles.btnApprove}
                  onClick={() => openConfirmDialog(0)}
                  disabled={updating}
                >
                  Aprovar
                </button>
                <button
                  className={styles.btnReject}
                  onClick={() => openConfirmDialog(2)}
                  disabled={updating}
                >
                  Recusar
                </button>
              </div>
            )}
            {selectedOrder && selectedOrder.pedido_situacao !== null && (() => {
              const badge = getBadgeProps(selectedOrder.pedido_situacao, selectedOrder.situacao_desc);
              return (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span className={`${styles.statusBadge} ${badge.className}`}>
                    {badge.text}
                  </span>
                  {renderEtapasDots(selectedOrder)}
                </div>
              );
            })()}
          </div>
        </div>

        {loadingDetails && <p className={styles.feedback}>Carregando detalhes...</p>}
        {error && <p className={styles.feedback} style={{ color: '#b91c1c' }}>{error}</p>}
        {!loadingDetails && !selectedOrder && !error && (
          <p className={styles.feedback}>Pedido não encontrado.</p>
        )}

        {selectedOrder && (
          <div className={styles.detailsGrid}>
            <div className={styles.colLeft}>
              <article className={styles.infoBlock}>
                <h4>
                  <PersonOutlineIcon fontSize="small" />
                  Cliente & Vendedor
                </h4>
                {loadingDetails ? (
                  <div className={styles.skeletonBlock}>
                    <Skeleton variant="text" width="100%" height={24} />
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="80%" height={24} />
                  </div>
                ) : (
                  <div className={styles.infoBlockContent}>
                    <p className={styles.fullWidth}><span>Razão social</span><strong>{selectedOrder.cliente_razao_social || '--'}</strong></p>
                    <p><span>CNPJ</span><strong>{formatCNPJ(selectedOrder.cliente_cnpj)}</strong></p>
                    <p><span>Cidade/UF</span><strong>{`${selectedOrder.cliente_cidade || '--'}/${selectedOrder.cliente_estado || '--'}`}</strong></p>
                    <p><span>Segmento</span><strong>{client?.SEGMENTO_CLASSE || '--'}</strong></p>
                    <p><span>Telefone</span><strong>{formatPhone(selectedOrder.cliente_telefone)}</strong></p>
                    <p><span>E-mail</span><strong>{client?.EMAIL || '--'}</strong></p>
                    <p><span>Vendedor</span><strong>{client?.VENDEDOR_NOME || '--'}</strong></p>
                    <p><span>Suporte</span><strong>{client?.BASE_PRODUCAO_NOME || '--'}</strong></p>
                  </div>
                )}
              </article>

              <article className={styles.infoBlock}>
                <h4>
                  <ReceiptLongOutlinedIcon fontSize="small" />
                  Pedido & Itens
                </h4>
                <div className={styles.infoBlockContent}>
                  <p><span>Data emissão</span><strong>{formatDate(selectedOrder.data_emissao)}</strong></p>
                  <p><span>Operação fiscal</span><strong>{selectedOrder.operacao_fiscal_desc ?? selectedOrder.operacoes_fiscais ?? '--'}</strong></p>
                  <p><span>Total</span><strong>{formatCurrency(calculatedTotal)}</strong></p>
                  <p><span>Cond. pagamento</span><strong>{selectedOrder.condicao_pagamento || '--'}</strong></p>
                </div>

                <h4 className={styles.subSectionTitle} style={{ marginTop: '20px' }}>
                  <Inventory2OutlinedIcon fontSize="small" />
                  Itens do Pedido
                </h4>
                {orderItems.length === 0 && (
                  <p><span>Itens</span><strong>Nenhum item vinculado.</strong></p>
                )}
                {orderItems.length > 0 && (
                  <div className={styles.itemsList}>
                    {orderItems.map((item) => (
                      <div key={item.id} className={styles.productRowHorizontal}>
                        <div className={styles.productIdCol}>
                          <span className={styles.productRefMini}>{item.referencia}</span>
                          <span className={styles.productDescCompact}>{item.descricao || 'Item sem descricao'}</span>
                        </div>

                        <div className={styles.productQtdCol}>
                          <div className={styles.miniFinItem}>
                            <span>Qtd</span>
                            <strong>{item.quantidade}</strong>
                          </div>
                        </div>

                        <div className={styles.productGradeCol}>
                          <div className={styles.gradeGridMini}>
                            {SIZES.map((size) => {
                              const g = item.grades.find((gr: any) => Number(gr.tamanho) === size);
                              const qty = g ? g.quantidade : 0;
                              return (
                                <div key={size} className={`${styles.miniGradeBox} ${qty === 0 ? styles.gradeHiddenMobile : ''}`}>
                                  <span className={styles.miniGradeSize}>{size}</span>
                                  <span className={styles.miniGradeQty} style={qty === 0 ? { color: '#cbd5e1' } : undefined}>{qty || '-'}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div className={styles.productTotalCol}>
                          <div className={styles.miniFinItem}>
                            <span>Subtotal</span>
                            <strong className={styles.trendValueCompact}>{formatCurrency(Number(item.subtotal || 0))}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            </div>

            <article className={styles.infoBlock} style={{ padding: '12px 14px' }}>
              <h4 style={{ marginBottom: '8px' }}>
                <ScienceOutlinedIcon fontSize="small" />
                Info. Amostra
              </h4>
              {loadingDetails ? (
                <div className={styles.skeletonBlock}>
                  <div className={styles.skeletonDetailGrid}>
                    <Skeleton variant="rectangular" height={40} />
                    <Skeleton variant="rectangular" height={40} />
                    <Skeleton variant="rectangular" height={40} />
                  </div>
                  <Skeleton variant="text" width="100%" height={60} className={styles.skeletonSpacingTop} />
                </div>
              ) : (
                <div className={styles.infoBlockContent} style={{ gridTemplateColumns: '1fr', gap: '6px' }}>
                  <p><span>Possui filial</span><strong>{sample?.possui_filial || '--'}</strong></p>
                  <p><span>Unidade compra</span><strong>{sample?.unidade_compra || '--'}</strong></p>
                  <p><span>Nº usuários</span><strong>{sample?.num_usuarios ?? '--'}</strong></p>
                  <p><span>Contato principal</span><strong>{sample?.contato_principal || '--'}</strong></p>
                  <p><span>Telefone</span><strong>{sample?.telefone_contato || '--'}</strong></p>
                  <p><span>E-mail</span><strong>{sample?.email_contato || '--'}</strong></p>
                  <p><span>CA/marca atual</span><ExpandableText text={sample?.ca_marca_atual} limit={60} /></p>
                  <p><span>Riscos da área</span><ExpandableText text={sample?.riscos_area} limit={60} /></p>
                  <p><span>Insatisfação</span><ExpandableText text={sample?.insatisfacao_atual} limit={60} /></p>
                  <p><span>Calçado sugerido</span><ExpandableText text={sample?.calcado_sugerido} limit={120} /></p>
                  <p><span>Observações</span><ExpandableText text={sample?.observacoes} />
                  </p>
                </div>
              )}
            </article>
          </div>
        )}
      </div>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      >
        <DialogTitle>
          {confirmDialog.status === 0 ? 'Confirmar Aprovação' : 'Confirmar Recusa'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja {confirmDialog.status === 0 ? 'aprovar' : 'recusar'} o pedido de amostra <strong>#{selectedOrder?.id || id}</strong>?
            {confirmDialog.status === 0 ? ' Esta ação mudará o status para Aprovado.' : ' Esta ação mudará o status para Recusado.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={executeUpdateStatus}
            color={confirmDialog.status === 0 ? "success" : "error"}
            variant="contained"
            autoFocus
          >
            {confirmDialog.status === 0 ? 'Sim, Aprovar' : 'Sim, Recusar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SampleOrderDetailsPage;
